'use client'

import React, { FC, PropsWithChildren, useMemo } from 'react'
import { notFound, redirect, usePathname } from 'next/navigation'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { getCurrentPage, getCustomerPages, isCustomerPageVisible } from '~/app/helpers'

import { User } from '~/hooks/useAuthentication/types'

import DesktopLayout from '~/app/(authenticated)/DesktopLayout'
import {
  CustomerLoadBasePath,
  IntegratedTrackingBasePath,
  ShipmentBasePath,
} from '~/constants/urls'
import MobileLayout from '~/app/(authenticated)/MobileLayout'
import { NETWORK } from '~/app/constants'

import { CustomerType } from '~/__generated_types__/globalTypes'

type Props = {
  user: NonNullable<User>
  device: { isDesktop: boolean }
}

const CustomerAuthenticatedShell: FC<PropsWithChildren<Props>> = ({
  children,
  user,
  device,
}) => {
  const pathname = usePathname()
  const flag = useFlags()

  const availablePages = useMemo(() => {
    return getCustomerPages(user.role, user.customer?.type).filter((page) =>
      isCustomerPageVisible(page, {
        flags: flag,
        customerName: user.customer?.name,
        hasSecureFilters: user.hasSecureFilters,
      })
    )
  }, [flag, user.role, user.customer?.type, user.customer?.name, user.hasSecureFilters])

  const currentPage = getCurrentPage(pathname, availablePages)

  if (!currentPage) {
    switch (user.customer?.type) {
      case CustomerType.Dts:
        redirect(CustomerLoadBasePath)
      case CustomerType.Tm:
        redirect(ShipmentBasePath)
      case CustomerType.Integrated:
        redirect(IntegratedTrackingBasePath)
      default:
        notFound()
    }
  }

  const isNetworkPage = currentPage.key === NETWORK
  const LayoutComponent =
    isNetworkPage && !device.isDesktop ? MobileLayout : DesktopLayout

  return (
    <LayoutComponent
      availablePages={availablePages}
      currentPage={currentPage}
      user={user}
    >
      {children}
    </LayoutComponent>
  )
}

export default CustomerAuthenticatedShell
